import { type ComponentPropsWithoutRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { EnterTracking } from '@/components/EnterTracking';
import Footer from '@/components/Footer';
import { useLocale } from '@/hooks';
import { useUser } from '@/lib/auth';
import { PricingSection3 } from '@/module/payment/Pricing/PricingSection3';
import { clearReferrer, DEFAULT_FROM, getReferrer } from '@/shared/features/enter-referrer-tracking';
import { useCountryCode } from '@/shared/hooks';
import { cn } from '@/utils/tailwind.util';
import { type GroupPricingContent, resolveGroupPricingContent } from './PricingSection1/groupPricingContent';
import { GroupPricingSection } from './PricingSection1/GroupPricingSection';
import { BulkAdoptionBanner } from './BulkAdoptionBanner';
import { PlanComparisonSection } from './PlanComparisonSection';
import { PricingSection2 } from './PricingSection2';
import { SchoolDocumentsSection } from './SchoolDocumentsSection';
import { SchoolUsageBanner } from './SchoolUsageBanner';
import { TryProForFreeBanner } from './TryProForFreeBanner';
const PLAN_COMPARISON_HASH = '#plan-comparison';
export const Pricing = ({
  hasSessionHint
}: {
  hasSessionHint: boolean;
}) => {
  const {
    i18n
  } = useTranslation(['pricing']);
  const {
    countryCode,
    isKR
  } = useCountryCode();
  const {
    locale
  } = useLocale();
  const {
    isAuthenticated
  } = useUser();
  const pricingReferrer = useMemo(() => getReferrer(), []);
  const groupPricingContent = useMemo(() => {
    const resources = i18n.getResourceBundle(locale, 'pricing') as Record<string, unknown> | undefined;
    return resolveGroupPricingContent(resources, countryCode);
  }, [countryCode, i18n, locale]);
  const showKoreanSchoolSections = isKR && locale === 'ko';

  // 랜딩 등 다른 페이지에서 router.push('/pricing#plan-comparison')로 넘어온 경우,
  // 브라우저 네이티브 fragment 스크롤이 클라이언트 사이드 내비게이션에선 동작하지 않으므로
  // 진입 시 해시를 직접 읽어 비교 섹션으로 스크롤한다. (같은 페이지 버튼은 각자 처리)
  useEffect(() => {
    if (window.location.hash !== PLAN_COMPARISON_HASH) return;
    const raf = requestAnimationFrame(() => {
      document.getElementById('plan-comparison')?.scrollIntoView({
        block: 'start'
      });
    });
    return () => cancelAnimationFrame(raf);
  }, []);
  return <EnterTracking eventName="enter_pricing" eventProperties={{
    ...(pricingReferrer?.from && pricingReferrer.from !== DEFAULT_FROM ? {
      from: pricingReferrer.from
    } : {}),
    ...(pricingReferrer?.location ? {
      location: pricingReferrer.location
    } : {})
  }} onTrack={clearReferrer} data-sentry-element="EnterTracking" data-sentry-component="Pricing" data-sentry-source-file="Pricing.tsx">
      <GroupPricingPage content={groupPricingContent} hasSessionHint={hasSessionHint} showKoreanSchoolSections={showKoreanSchoolSections} showTryProBanner={!isAuthenticated && !isKR} data-sentry-element="GroupPricingPage" data-sentry-source-file="Pricing.tsx" />
    </EnterTracking>;
};
const GroupPricingPage = ({
  content,
  hasSessionHint,
  showKoreanSchoolSections,
  showTryProBanner
}: {
  content: GroupPricingContent;
  hasSessionHint: boolean;
  showKoreanSchoolSections: boolean;
  showTryProBanner: boolean;
}) => {
  return <>
      <GroupPricingSection content={content} hasSessionHint={hasSessionHint} data-sentry-element="GroupPricingSection" data-sentry-source-file="Pricing.tsx" />
      {showKoreanSchoolSections && <SchoolUsageBanner />}
      <BulkAdoptionBanner data-sentry-element="BulkAdoptionBanner" data-sentry-source-file="Pricing.tsx" />
      <PricingSection2 data-sentry-element="PricingSection2" data-sentry-source-file="Pricing.tsx" />
      <PlanComparisonSection groupPricingContent={content} data-sentry-element="PlanComparisonSection" data-sentry-source-file="Pricing.tsx" />
      {showKoreanSchoolSections && <SchoolDocumentsSection />}
      <PricingSection3 data-sentry-element="PricingSection3" data-sentry-source-file="Pricing.tsx" />
      {showTryProBanner && <TryProForFreeBanner />}
      <Footer data-sentry-element="Footer" data-sentry-source-file="Pricing.tsx" />
    </>;
};
export const PricingSection = (props: ComponentPropsWithoutRef<'section'>) => {
  const {
    children,
    ...rest
  } = props;
  return <section {...rest} className={cn('flex flex-col self-stretch items-center word-break-all', 'max-[890px]:px-xl min-[891px]:px-3xl', 'py-md min-[750px]:py-2xl min-[751px]:py-3xl', props.className)} data-sentry-component="PricingSection" data-sentry-source-file="Pricing.tsx">
      {children}
    </section>;
};